import {useState, useEffect} from 'react';
import {connect} from 'react-redux';
import {Field, FormSection, getFormValues, change} from 'redux-form';
import SelectField from 'App/formComponents/fields/select';
import InputField from 'App/formComponents/fields/input';
import {registration} from 'App/api';
import {truncateNumber} from 'App/utils/globals';
import {fedexServiceOptions, upsServiceOptions, uspsServiceOptions} from 'App/utils/parcelConstants';
import {countries} from 'App/utils/countries';
import './styles.scss';

const ShipmentServices = (props) => {
  const {values = {}, form, isSlimForm, parcelProviderOptions, dispatch, triggerFieldTouch, setFieldValue} = props;
  const isFedExParcel = values.fedex_specific_options;
  const isUPSParcel = values.ups_specific_options;
  const isUSPSParcel = values.usps_specific_options;
  const [accounts, setAccounts] = useState([]);

  let parcelLabel = 'FedEx';
  let serviceCodeOptions = fedexServiceOptions;

  if (isUPSParcel) {
    serviceCodeOptions = upsServiceOptions;
    parcelLabel = 'UPS';
  }

  if (isUSPSParcel) {
    serviceCodeOptions = uspsServiceOptions;
    parcelLabel = 'USPS';
  }
  const providerOptionsField = `${parcelLabel.toLowerCase()}_specific_options`;
  const has3rdPartyBilling = (billingType) => {
    return billingType && ['COLLECT', 'RECIPIENT', 'THIRD_PARTY'].includes(billingType);
  };
  const onBillingTypeChange = (value) => {
    if (value === 'SENDER') {
      setFieldValue(`${providerOptionsField}.bill_to_account_number`, '');
      setFieldValue(`${providerOptionsField}.bill_to_country_code`, '');
      setFieldValue(`${providerOptionsField}.bill_to_postal_code`, '');
    } else if (has3rdPartyBilling(value) && !values?.[providerOptionsField]?.bill_to_country_code) {
      setFieldValue(`${providerOptionsField}.bill_to_country_code`, 'US');
    }
  };

  /**
   * Request FedEx accounts
   */
  const getFedExRegistrationAccounts = async () => {
    try {
      const response = await registration.getFedExRegistrationAccounts();

      if (response && response.body && response.body.results.length) {
        setAccounts(response.body.results.map((account) => ({id: account.id, name: account.account_number})));
      }
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * Request UPS accounts
   */
  const getUPSRegistrationAccounts = async () => {
    try {
      const response = await registration.getUPSRegistrationAccounts();

      if (response && response.body && response.body.results.length) {
        setAccounts(response.body.results.map((account) => ({id: account.id, name: account.account_number})));
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    const providers = parcelProviderOptions.map(({id}) => id);
    const selectedProviders = new Set();

    if (isSlimForm && values.parcel_provider && Array.isArray(values.parcel_provider)) {
      providers.forEach((provider) => {
        if (values.parcel_provider.find(({id}) => id === provider)) {
          selectedProviders.add(provider);
          dispatch(change(form, `${provider}_specific_options`, {}));
        } else {
          dispatch(change(form, `${provider}_specific_options`, null));
        }
      });
    }
  }, [values.parcel_provider]);

  useEffect(() => {
    if (!isSlimForm) {
      if (isFedExParcel) {
        getFedExRegistrationAccounts();
      }
      if (isUPSParcel) {
        getUPSRegistrationAccounts();
      }
    }
  }, [isFedExParcel, isUPSParcel, isSlimForm]);

  return (
    <>
      {isSlimForm && (
        <div className="field-grid">
          <div className="grid-item-2">
            <Field
              multi
              label="Parcel Provider"
              name="parcel_provider"
              placeholder="Parcel Provider"
              options={parcelProviderOptions}
              component={SelectField}
            />
          </div>
        </div>
      )}
      <FormSection name={providerOptionsField}>
        <div className="field-grid">
          {!isSlimForm && (
            <>
              {!isUSPSParcel && (
                <div className="grid-item-2">
                  <Field
                    req
                    simpleValue
                    label={`${parcelLabel} Account`}
                    name="account"
                    placeholder={`${parcelLabel} Account`}
                    options={accounts}
                    component={SelectField}
                  />
                </div>
              )}
              <div className="grid-item-2">
                <Field
                  simpleValue
                  label="Standard Service"
                  name="service_code"
                  placeholder="Standard Service"
                  options={serviceCodeOptions}
                  component={SelectField}
                />
              </div>
              <div className="grid-item-2">
                <Field
                  simpleValue
                  clearable
                  label="Signature Type"
                  name="signature_option_signature_type"
                  placeholder="Signature Type"
                  options={[
                    {id: 'SERVICE_DEFAULT', name: 'Service Default'},
                    {id: 'NO_SIGNATURE', name: 'No Signature Required'},
                    {id: 'INDIRECT_SIGNATURE', name: 'Indirect Signature Required'},
                    {id: 'DIRECT_SIGNATURE', name: 'Direct Signature Required'},
                    {id: 'ADULT_SIGNATURE', name: 'Adult Signature Required'}
                  ]}
                  component={SelectField}
                />
              </div>
              {isFedExParcel && (
                <div className="field-grid grid-item-2">
                  <div className="grid-item-1">
                    <Field
                      simpleValue
                      label="Dropoff Options"
                      name="dropoff_type"
                      placeholder="Dropoff Options"
                      options={[
                        {id: 'REGULAR_PICKUP', name: 'Regular Pickup'},
                        {id: 'STATION', name: 'Station'},
                        {id: 'REQUEST_COURIER', name: 'Request Courier'},
                        {id: 'BUSINESS_SERVICE_CENTER', name: 'Business Service Center'},
                        {id: 'DROP_BOX', name: 'Drop Box'}
                      ]}
                      component={SelectField}
                    />
                  </div>
                </div>
              )}
              <div className="grid-item-2 card-subheader">
                <strong>Billing Options</strong>
              </div>
              <>
                <div className="grid-item-1">
                  <Field
                    simpleValue
                    label="Bill To Payment Options"
                    name="bill_to_payment_type"
                    placeholder="Bill To Payment Options"
                    options={[
                      {id: 'COLLECT', name: 'Collect'},
                      {id: 'RECIPIENT', name: 'Recipient'},
                      {id: 'SENDER', name: 'Sender'},
                      {id: 'THIRD_PARTY', name: 'Third Party'}
                    ]}
                    onChange={(e, value) => {
                      onBillingTypeChange(value);
                    }}
                    component={SelectField}
                  />
                </div>
                {has3rdPartyBilling(values?.[providerOptionsField]?.bill_to_payment_type) && (
                  <>
                    <div className="grid-item-1">
                      <Field
                        req
                        label="Bill To Account ID"
                        name="bill_to_account_number"
                        placeholder="Account ID"
                        component={InputField}
                      />
                    </div>
                    <div className="grid-item-1">
                      <Field
                        req
                        label="Country"
                        name="bill_to_country_code"
                        placeholder="Country"
                        labelKey="label"
                        valueKey="value"
                        simpleValue
                        options={countries}
                        component={SelectField}
                      />
                    </div>
                    <div className="grid-item-1">
                      <Field
                        req
                        label="Zip Code"
                        name="bill_to_postal_code"
                        placeholder="Zip Code"
                        component={InputField}
                      />
                    </div>
                  </>
                )}
              </>
              <div className="grid-item-2 card-subheader">
                <strong>Additional Services</strong>
              </div>
              {isFedExParcel && (
                <>
                  <div className="grid-item-1">
                    <Field
                      simpleValue
                      clearable
                      label="Battery Material Type"
                      name="battery_material_type"
                      placeholder="Battery Material Type"
                      options={[
                        {id: 'LITHIUM_ION', name: 'Lithium Ion'},
                        {id: 'LITHIUM_METAL', name: 'Lithium Metal'}
                      ]}
                      component={SelectField}
                    />
                  </div>
                  <div className="grid-item-1">
                    <Field
                      simpleValue
                      clearable
                      label="Battery Packaging Type"
                      name="battery_packaging_type"
                      placeholder="Battery Packaging Type"
                      options={[
                        {id: 'CONTAINED_IN_EQUIPMENT', name: 'Contained in Equipment'},
                        {id: 'PACKED_WITH_EQUIPMENT', name: 'Packed with Equipment'}
                      ]}
                      component={SelectField}
                    />
                  </div>
                </>
              )}
              {!isUSPSParcel && (
                <div className="field-grid grid-item-2">
                  <div className="grid-item-1">
                    <Field
                      simpleValue
                      clearable
                      label="Alcohol Recipient Type"
                      name="alcohol_recipient_type"
                      placeholder="Alcohol Recipient Type"
                      options={[
                        {id: 'CONSUMER', name: 'Consumer'},
                        {id: 'LICENSEE', name: 'Licensee'}
                      ]}
                      component={SelectField}
                    />
                  </div>
                </div>
              )}
              {/* {isFedExParcel && (
                <div className="grid-item-1">
                  <Field
                    simpleValue
                    clearable
                    label="Home Delivery Options"
                    name="home_delivery_type"
                    placeholder="Home Delivery Options"
                    options={[
                      {id: 'APPOINTMENT', name: 'FedEx Appointment Home Delivery®'},
                      {id: 'DATE_CERTAIN', name: 'FedEx Date Certain Home Delivery®'},
                      {id: 'EVENING', name: 'FedEx Evening Home Delivery®'}
                    ]}
                    component={SelectField} />
                </div>
              )} */}
              <div className="field-grid grid-item-2">
                <div className="grid-item-1">
                  <Field
                    simpleValue
                    clearable
                    label="C.O.D Options"
                    name="cod_collection_type"
                    placeholder="C.O.D Options"
                    options={[
                      {id: 'ANY', name: 'Any'},
                      {id: 'COMPANY_CHECK', name: 'Company Check'},
                      {id: 'GUARANTEED_FUNDS', name: 'Guaranteed Funds'},
                      {id: 'PERSONAL_CHECK', name: 'Personal Check'}
                    ]}
                    onChange={() => {
                      // set related field touched so that validation error displays before submit
                      triggerFieldTouch(`[${providerOptionsField}].cod_collection_amount`);
                    }}
                    component={SelectField}
                  />
                </div>
                <div className="grid-item-1">
                  <Field
                    req={values?.[providerOptionsField]?.cod_collection_type}
                    label="Amount to Collect on Delivery ($ USD)"
                    name="cod_collection_amount"
                    placeholder="Amount"
                    component={InputField}
                    normalize={(value) => (value === '' ? null : truncateNumber(value))}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </FormSection>
    </>
  );
};

export default connect((state, props) => ({
  values: getFormValues(props.form)(state) || {},
  parcelProviderOptions: [
    state.userCompany.company.feature_flags &&
      state.userCompany.company.feature_flags.fedex_enabled && {id: 'fedex', name: 'FedEx'},
    state.userCompany.company.feature_flags &&
      state.userCompany.company.feature_flags.ups_enabled && {id: 'ups', name: 'UPS'},
    state.userCompany.company.feature_flags &&
      state.userCompany.company.feature_flags.usps_enabled && {id: 'usps', name: 'USPS'}
  ].filter((v) => v)
}))(ShipmentServices);
